/**
 * 포맷팅 유틸리티 함수
 * 날짜, 기간, 숫자 등의 포맷팅을 담당
 */

/**
 * 기간 문자열로 변환 (YYYYMM 형식)
 * @param {string|number} value - 기간 값
 * @returns {string} YYYYMM 형식의 기간 문자열
 */
export function toPeriodString(value) {
    if (!value) return "";
    if (/^\d{6}$/.test(value)) return value;
    return String(value).replace(/[^0-9]/g, "").slice(0, 6);
}

/**
 * 기간을 표시용 형식으로 변환 (YYYY-MM 형식)
 * @param {string|number} value - 기간 값
 * @returns {string} YYYY-MM 형식의 기간 문자열
 */
export function toDisplayPeriod(value) {
    if (!value) return "-";
    const period = toPeriodString(value);
    if (period.length !== 6) return String(value);
    return `${period.slice(0, 4)}-${period.slice(4, 6)}`;
}

/**
 * 숫자를 천 단위 구분자와 함께 포맷팅
 * @param {number} value - 숫자 값
 * @param {number} decimals - 소수점 자릿수
 * @returns {string} 포맷팅된 문자열
 */
export function formatNumber(value, decimals = 0) {
    if (value == null || isNaN(value)) return "-";
    return Number(value).toLocaleString("ko-KR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

/**
 * 배출량 포맷팅 (tCO₂e 단위)
 * @param {number} value - 배출량 값
 * @param {number} decimals - 소수점 자릿수
 * @returns {string} 포맷팅된 배출량 문자열
 */
export function formatEmission(value, decimals = 2) {
    return `${formatNumber(value, decimals)} tCO₂e`;
}

/** API에서 오는 단위 (예: kgCO2e). 없으면 kgCO2e로 간주 */
const DEFAULT_API_UNIT = "kgCO2e";
/** 표시용 톤 단위 */
export const DISPLAY_UNIT_TON = "tCO2e";
/** 표시용 kg 단위 */
export const DISPLAY_UNIT_KG = "kgCO2e";

/**
 * API 단위 기준 값을 표시 단위로 변환한 숫자 반환 (차트/계산용)
 * @param {number} value - 배출량 값 (API 단위)
 * @param {string} [apiUnit] - API 단위 (예: kgCO2e). 없으면 kgCO2e
 * @param {string} [displayUnit] - 표시 단위 (kgCO2e | tCO2e)
 * @returns {number}
 */
export function toDisplayValue(value, apiUnit = DEFAULT_API_UNIT, displayUnit = DEFAULT_API_UNIT) {
    if (value == null || typeof value !== "number") return value;
    const api = (apiUnit || DEFAULT_API_UNIT).toLowerCase();
    const display = (displayUnit || DEFAULT_API_UNIT).toLowerCase();
    if (display === "tco2e" && (api === "kgco2e" || !api)) {
        return value / 1000;
    }
    return value;
}

/**
 * 표시할 단위 라벨 반환 (API 단위 + 표시 단위 선택에 따라)
 * @param {string} [apiUnit] - API 단위
 * @param {string} [displayUnit] - 표시 단위 (kgCO2e | tCO2e)
 * @returns {string}
 */
export function getDisplayUnitLabel(apiUnit = DEFAULT_API_UNIT, displayUnit = DEFAULT_API_UNIT) {
    const display = (displayUnit || DEFAULT_API_UNIT).toLowerCase();
    if (display === "tco2e") return "tCO2e";
    return apiUnit || DEFAULT_API_UNIT;
}

/**
 * 배출량 값을 표시 단위에 맞게 포맷 (숫자만, 천단위 구분)
 * @param {number} value - 배출량 값 (API 단위)
 * @param {string} [apiUnit] - API 단위. 없으면 kgCO2e
 * @param {string} [displayUnit] - 표시 단위 (kgCO2e | tCO2e)
 * @param {number} [decimals=2] - 소수 자릿수
 * @returns {string} 포맷된 숫자 문자열 (단위 제외)
 */
export function formatEmissionValue(value, apiUnit = DEFAULT_API_UNIT, displayUnit = DEFAULT_API_UNIT, decimals = 2) {
    if (value == null || typeof value !== "number") return "—";
    const num = toDisplayValue(value, apiUnit, displayUnit);
    return formatNumber(num, decimals);
}

/**
 * 배출량 포맷 + 표시 단위 (문자열 한 덩어리)
 * @param {number} value - 배출량 값 (API 단위)
 * @param {string} [apiUnit] - API 단위
 * @param {string} [displayUnit] - 표시 단위
 * @param {number} [decimals=2] - 소수 자릿수
 * @returns {{ text: string, unit: string }}
 */
export function formatEmissionWithUnit(value, apiUnit = DEFAULT_API_UNIT, displayUnit = DEFAULT_API_UNIT, decimals = 2) {
    const text = formatEmissionValue(value, apiUnit, displayUnit, decimals);
    const unit = getDisplayUnitLabel(apiUnit, displayUnit);
    return { text, unit };
}

/**
 * 퍼센트 포맷팅
 * @param {number} value - 퍼센트 값 (0-100)
 * @param {number} decimals - 소수점 자릿수
 * @returns {string} 포맷팅된 퍼센트 문자열
 */
export function formatPercent(value, decimals = 1) {
    if (value == null || isNaN(value)) return "-";
    return `${Number(value).toFixed(decimals)}%`;
}
